import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  SelectChangeEvent,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { apiService, Client, Bundle } from '../services/api';

interface EditClientModalProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
  adminSecret: string;
  onClientUpdated: () => void;
}

const EditClientModal: React.FC<EditClientModalProps> = ({
  open,
  onClose,
  client,
  adminSecret,
  onClientUpdated
}) => {
  const [newSecret, setNewSecret] = useState('');
  const [operatorSpaceLink, setOperatorSpaceLink] = useState('');
  const [selectedBundles, setSelectedBundles] = useState<Bundle[]>([]);
  const [currentBundleId, setCurrentBundleId] = useState<number | ''>('');
  const [availableBundles, setAvailableBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingClient, setIsUpdatingClient] = useState(false);
  const [error, setError] = useState('');
  const [showCurrentSecret, setShowCurrentSecret] = useState(false);

  // Populate form when client changes or modal opens
  useEffect(() => {
    if (client && open) {
      setOperatorSpaceLink(client.tx_operators_space_link || '');
      setSelectedBundles(client.bundles || []);
      fetchBundles();
    }
  }, [client, open, adminSecret]);

  const fetchBundles = async () => {
    try {
      const bundles = await apiService.getAllBundles(adminSecret);
      setAvailableBundles(bundles);
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
    }
  };

  const handleBundleSelect = (event: SelectChangeEvent<number | ''>) => {
    const bundleId = event.target.value;
    if (bundleId !== '') {
      const bundleToAdd = availableBundles.find(b => b.id === bundleId);
      if (bundleToAdd && !selectedBundles.find(b => b.id === bundleToAdd.id)) {
        setSelectedBundles([...selectedBundles, bundleToAdd]);
        setCurrentBundleId('');
      }
    }
  };

  const handleRemoveBundle = (bundleId: number) => {
    setSelectedBundles(selectedBundles.filter(b => b.id !== bundleId));
  };

  const handleUpdateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;

    setIsUpdatingClient(true);
    setError('');

    try {
      const bundleIds = selectedBundles.map(b => b.id);
      await apiService.updateClient(
        adminSecret,
        client.id,
        operatorSpaceLink || undefined,
        bundleIds
      );
      onClientUpdated();
      onClose();
    } catch (error: any) {
      setError(error?.response?.data?.detail || 'Failed to update client');
    } finally {
      setIsUpdatingClient(false);
    }
  };

  const handleUpdateSecret = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client) return;

    setIsLoading(true);
    setError('');

    try {
      await apiService.updateClientSecret(adminSecret, client.id, newSecret);
      onClientUpdated();
      setNewSecret('');
      onClose();
    } catch (error: any) {
      setError(error?.response?.data?.detail || 'Failed to update client secret');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewSecret('');
    setOperatorSpaceLink('');
    setSelectedBundles([]);
    setCurrentBundleId('');
    setError('');
    setShowCurrentSecret(false);
    onClose();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (!client) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Edit Client: {client.tx_name}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Client Information
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            ID: {client.id}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Name: {client.tx_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Operator Space Link: {client.tx_operators_space_link || 'Not set'}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Bundles: {client.bundles?.length || 0} bundle(s) associated
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current Client Secret
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              type={showCurrentSecret ? 'text' : 'password'}
              value={showCurrentSecret ? client.tx_client_secret : '••••••••••••••••••••••••••••••••'}
              InputProps={{ readOnly: true }}
              variant="outlined"
              size="small"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowCurrentSecret(!showCurrentSecret)}
            >
              {showCurrentSecret ? 'Hide' : 'Show'}
            </Button>
            {showCurrentSecret && (
              <IconButton onClick={() => copyToClipboard(client.tx_client_secret)}>
                <ContentCopyIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box component="form" onSubmit={handleUpdateClient} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Update Client Details
          </Typography>
          
          <TextField
            fullWidth
            label="Operator Space Link (Optional)"
            value={operatorSpaceLink}
            onChange={(e) => setOperatorSpaceLink(e.target.value)}
            margin="normal"
            disabled={isUpdatingClient}
            placeholder="https://example.com/space"
            helperText="Optional link to the operator's space"
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Client Bundles
          </Typography>

          {selectedBundles.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              {selectedBundles.map((bundle) => (
                <Chip
                  key={bundle.id}
                  label={`${bundle.tx_name} (v${bundle.tx_version})`}
                  onDelete={() => handleRemoveBundle(bundle.id)}
                  deleteIcon={<DeleteIcon />}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    borderColor: '#9ACD32',
                    color: '#9ACD32',
                    '& .MuiChip-deleteIcon': {
                      color: '#9ACD32',
                      '&:hover': {
                        color: '#7BA428'
                      }
                    }
                  }}
                />
              ))}
            </Stack>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="bundle-select-label">Select Bundle to Add</InputLabel>
            <Select
              labelId="bundle-select-label"
              value={currentBundleId}
              onChange={handleBundleSelect}
              label="Select Bundle to Add"
              disabled={isUpdatingClient}
            >
              <MenuItem value="">
                <em>Choose a bundle...</em>
              </MenuItem>
              {availableBundles
                .filter(bundle => !selectedBundles.find(sb => sb.id === bundle.id))
                .map((bundle) => (
                  <MenuItem key={bundle.id} value={bundle.id}>
                    {bundle.tx_name} (v{bundle.tx_version})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {selectedBundles.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedBundles.length} bundle(s) selected
            </Typography>
          )}

          <Box sx={{ mb: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdatingClient}
              sx={{ mr: 1 }}
            >
              {isUpdatingClient ? 'Updating...' : 'Update Client Details'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box component="form" onSubmit={handleUpdateSecret}>
          <Typography variant="h6" gutterBottom>
            Update Client Secret
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              label="New Client Secret"
              value={newSecret}
              onChange={(e) => setNewSecret(e.target.value)}
              required
              disabled={isLoading}
              helperText="Enter a new secret for this client"
            />
            <IconButton onClick={() => copyToClipboard(newSecret)} disabled={!newSecret}>
              <ContentCopyIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdateSecret}
          variant="contained"
          disabled={isLoading || !newSecret}
        >
          {isLoading ? 'Updating...' : 'Update Secret'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditClientModal;